// @ts-nocheck

import { colors } from '@lmc/common/constants'
import { IconType, Text, Icon as UiKitIcon } from '@lmc/ui-kit'
import { Heading } from '@lmc/ui-kit/src/general'
import Field from 'components/fields'
import dayjs from 'dayjs'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { NAMESPACE } from 'locale'
import { carePlansConstants } from 'locale/carePlans'
import React, {
  type CSSProperties,
  Suspense,
  useCallback,
  useEffect,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Icon } from '../generic'

type Props = {
  fields?: any
  formId?: string
  setFormField?: any
  style?: CSSProperties
  date?: string | null
  noFurtherSupportRequired?: boolean
}

const FIELD_ID = 'nextReviewDate'
const COMMENT_FIELD_ID = 'newComment'

const SIX_MONTH_REVIEW_FLAG = 'care_plan_six_month_review'

const RADIOS = [
  { id: 'week', label: '1 week' },
  { id: 'month', label: '1 month' },
  { id: 'three-months', label: '3 months' },
  { id: 'six-months', label: '6 months' },
  { id: 'no-identified-need' }
]

export default function CarePlanNextReviewField({
  fields,
  formId,
  setFormField,
  style,
  date,
  noFurtherSupportRequired
}: Props) {
  const { t } = useTranslation(NAMESPACE.CARE_PLANS)
  const [selectedRadio, setSelectedRadio] = useState(null)
  const { data: flags } = useFeatureFlags({
    flagNames: [SIX_MONTH_REVIEW_FLAG]
  })
  const showSixMonths = flags[SIX_MONTH_REVIEW_FLAG].enabled

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (fields?.noFurtherSupportRequired) {
      setSelectedRadio('no-identified-need')
    }
  }, [fields?.noFurtherSupportRequired])

  const handleRadioClick = useCallback(
    (id) => {
      if (selectedRadio === id) return
      setSelectedRadio(id)
      const isNoFurtherSupport = id === 'no-identified-need'

      const times = {
        'no-identified-need': null,
        week: dayjs().add(1, 'weeks').format('DD-MM-YYYY'),
        month: dayjs().add(1, 'months').format('DD-MM-YYYY'),
        'three-months': dayjs().add(3, 'months').format('DD-MM-YYYY'),
        'six-months': dayjs().add(6, 'months').format('DD-MM-YYYY')
      }

      setFormField({
        key: FIELD_ID,
        value: times[id]
      })
      setFormField({
        key: 'noFurtherSupportRequired',
        value: isNoFurtherSupport
      })
    },
    [selectedRadio, setFormField]
  )

  const renderRadios = () =>
    RADIOS.filter(({ id }) => showSixMonths || id !== 'six-months').map(
      ({ id, label }) => {
        const displayLabel =
          label ?? t(carePlansConstants.NO_FURTHER_SUPPORT_REQUIRED)

        return (
          <RadioOption
            data-cy={`add-${id}`}
            id={`add-${id}`}
            key={`add-${id}`}
            onClick={() => handleRadioClick(id)}
          >
            <Icon
              name={id === selectedRadio ? 'radio-on' : 'radio-off'}
              fill={id === selectedRadio ? colors.pink : null}
              size={24}
            />
            <RadioOptionLabel data-cy={`add-${id}`}>
              {displayLabel}
            </RadioOptionLabel>
          </RadioOption>
        )
      }
    )

  const renderDateField = () => {
    // Keep the picker mounted (disabled) when no further support is
    // required, so toggling the radios doesn't shift the layout
    const noFurtherSupport = !!fields.noFurtherSupportRequired

    return (
      <Suspense fallback={<div>Loading...</div>}>
        <NextReviewFieldInput>
          <Field
            disabled={noFurtherSupport}
            displayCalendar
            futureDate
            keys={FIELD_ID}
            label="Next review date"
            layout="no-label"
            formId={formId}
            required={!noFurtherSupport}
            placeholder="DD-MM-YYYY"
            type="date"
            validationTypes={['validDate', 'futureDate']}
            values={fields}
          />
        </NextReviewFieldInput>
      </Suspense>
    )
  }

  const renderViewMode = () => {
    const displayText = noFurtherSupportRequired
      ? t(carePlansConstants.NO_FURTHER_SUPPORT_REQUIRED)
      : date
        ? dayjs(date).format('DD-MM-YYYY')
        : t(carePlansConstants.NEXT_REVIEW_NO_DATE_SET)

    return (
      <ViewMainCard className="lmc-card" style={style}>
        <ViewTitleRow>
          <ViewSectionHeading>
            {t(carePlansConstants.NEXT_REVIEW)}
          </ViewSectionHeading>
          <ViewDateRow>
            <ViewCalendarIcon customIcon={IconType.Calendar} />
            <ViewDateDisplay id="next-review-date">
              {displayText}
            </ViewDateDisplay>
          </ViewDateRow>
        </ViewTitleRow>
      </ViewMainCard>
    )
  }

  const title = t(carePlansConstants.NEXT_REVIEW)

  if (!formId) {
    return renderViewMode()
  }

  return (
    <EditModeCard className="lmc-card" style={style}>
      <CommentsBlock>
        <Heading level={5} weight="medium">
          Any additional comments on this review?
        </Heading>
        <Suspense fallback={<div>Loading...</div>}>
          <CommentFieldWrapper>
            <Field
              keys={COMMENT_FIELD_ID}
              label={COMMENT_FIELD_ID}
              layout="no-label"
              formId={formId}
              type="textarea"
              values={fields}
            />
          </CommentFieldWrapper>
        </Suspense>
      </CommentsBlock>
      <NextReviewToolbar>
        <NextReviewTitle>{title}</NextReviewTitle>
        <DateRow>
          <EditCalendarIcon customIcon={IconType.Calendar} />
          {renderDateField()}
        </DateRow>
        <RadioGroup>{renderRadios()}</RadioGroup>
      </NextReviewToolbar>
    </EditModeCard>
  )
}

const CommentsBlock = styled.div`
  align-self: stretch;
  width: 100%;
`

const CommentFieldWrapper = styled.div`
  margin-bottom: 20px;
`

const DateRow = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin: 3px 0;
`

const EditCalendarIcon = styled(UiKitIcon)`
  align-self: center;
  height: 24px;
  margin-left: 5px;
  width: 24px;
`

const EditModeCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 13px 25px;
`

const NextReviewFieldInput = styled.div`
  margin-right: 20px;
  padding-left: 15px;
`

const NextReviewTitle = styled(Text)`
  display: block;
  align-self: center;
  flex-shrink: 0;
  margin: 3px 20px 3px 0;
`

const NextReviewToolbar = styled.div`
  align-items: center;
  column-gap: 8px;
  display: flex;
  flex-flow: row wrap;
  margin-right: 50px;
  row-gap: 8px;
  width: 100%;
`

const RadioGroup = styled.div`
  align-items: center;
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  min-width: 0;
`

const RadioOption = styled.div`
  align-self: center;
  cursor: pointer;
  display: flex;
  height: 24px;
  margin-right: 20px;
`

const RadioOptionLabel = styled.span`
  align-self: center;
  display: block;
  margin-left: 5px;
`

const ViewCalendarIcon = styled(UiKitIcon)`
  height: 33px;
  width: 33px;
`

const ViewDateDisplay = styled.span`
  align-self: center;
  color: #000;
  display: block;
  opacity: 0.7;
  padding-left: 15px;
`

const ViewDateRow = styled.div`
  display: flex;
  margin: 3px 50px 3px 0;
`

const ViewMainCard = styled.div`
  align-content: space-between;
  display: flex;
  flex-flow: column wrap;
  padding: 5px 25px;
`

const ViewSectionHeading = styled.span`
  align-self: center;
  display: block;
  font-weight: bold;
  margin: 3px 20px 3px 0;
`

const ViewTitleRow = styled.div`
  display: flex;
  flex-flow: row wrap;
  min-height: 42px;
`
